import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import { UpgradePrompt } from "./UpgradePrompt";
import { Shell } from "@/components/layout/Shell";
import { Skeleton } from "@/components/ui/skeleton";

interface CustomPlanFeatureRouteProps {
  /**
   * Feature key(s) required for access. For custom plans, checks if the user
   * has selected these features. All features must be present (AND logic).
   */
  requiredFeatures: string[];
  children: ReactNode;
  redirectTo?: string;
  featureName?: string;
}

/**
 * Route guard for custom plans that checks multiple feature keys.
 * Unlike FeatureRoute (which checks subscription-tier features),
 * this specifically validates custom plan feature selections.
 */
export function CustomPlanFeatureRoute({ 
  requiredFeatures, 
  children, 
  redirectTo,
  featureName 
}: CustomPlanFeatureRouteProps) {
  const { isLoading, isCustomPlan, hasCustomPlanFeature } = useFeatureAccess();

  if (isLoading) {
    return (
      <Shell>
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-[400px] w-full" />
        </div>
      </Shell>
    );
  }

  // For custom plans, check if user has ALL required features
  if (isCustomPlan) {
    const hasAllFeatures = requiredFeatures.every(f => hasCustomPlanFeature(f));
    
    if (!hasAllFeatures) {
      if (redirectTo) {
        return <Navigate to={redirectTo} replace />;
      }

      return (
        <Shell>
          <UpgradePrompt 
            feature={featureName || requiredFeatures.join(' & ')} 
            variant="card"
            className="max-w-2xl mx-auto mt-20"
          />
        </Shell>
      );
    }
  }

  // For non-custom plans, allow access (regular FeatureRoute handles tier-based gating)
  return <>{children}</>;
}
